import { useEffect, useState } from "react"
import type { MultiFieldAnswer, MultiFieldDefinition } from "@/types/agent"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { TextInput } from "../ui/text-input"

interface QuestionMultiFieldInputProps {
  value: MultiFieldAnswer | undefined
  onChange: (value: MultiFieldAnswer) => void
  disabled?: boolean
  fields: MultiFieldDefinition[]
}

function getFieldDefaults(fields: MultiFieldDefinition[]): MultiFieldAnswer {
  const defaults: MultiFieldAnswer = {}
  for (const field of fields) {
    if (field.default !== undefined) {
      defaults[field.id] = field.default
    }
  }
  return defaults
}

function getNumericValue(
  val: string | number | boolean | undefined
): number | "" {
  return typeof val === "number" ? val : ""
}

export function QuestionMultiFieldInput({
  value,
  onChange,
  disabled,
  fields,
}: QuestionMultiFieldInputProps) {
  const [fieldValues, setFieldValues] = useState<MultiFieldAnswer>(() => {
    const defaults = getFieldDefaults(fields)
    return { ...defaults, ...value }
  })

  useEffect(() => {
    onChange(fieldValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues])

  const updateField = (id: string, newValue: string | number | boolean) => {
    setFieldValues((prev) => ({
      ...prev,
      [id]: newValue,
    }))
  }

  if (!fields || fields.length === 0) {
    return <div className="text-sm text-gray-10">No fields configured</div>
  }

  return (
    <div className="flex flex-wrap gap-x-2.5 gap-y-4">
      {fields.map((field) => (
        <div key={field.id} className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">{field.label}</label>
          {field.type === "boolean" && (
            <div className="flex items-center gap-2">
              <Switch
                checked={fieldValues[field.id] === true}
                onCheckedChange={(checked) => updateField(field.id, checked)}
                disabled={disabled}
                size="sm"
              />
              <span className="text-sm">
                {fieldValues[field.id] === true ? "Yes" : "No"}
              </span>
            </div>
          )}
          {field.type === "number" && (
            <TextInput
              size="sm"
              type="number"
              value={getNumericValue(fieldValues[field.id])}
              onChange={(e) => {
                const val = e.target.value
                updateField(field.id, val === "" ? "" : parseInt(val, 10))
              }}
              disabled={disabled}
              min={0}
              className="w-32 text-center"
            />
          )}
          {field.type === "dropdown" && field.options && (
            <Select
              value={String(fieldValues[field.id] ?? "")}
              onValueChange={(newValue) =>
                newValue != null && updateField(field.id, newValue)
              }
              disabled={disabled}
            >
              <SelectTrigger size="sm" className="w-fit min-w-32">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {field.options.map((option, idx) => (
                  <SelectItem key={idx} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ))}
    </div>
  )
}
